import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Anexo do "Contrato assinado" nos checklists de admissão e rescisão.
// O PDF vai para o bucket GCS (privado) — o backend não deixa concluir a tarefa
// sem anexo. O tipo de contrato (CLT, PJ, Jovem Aprendiz...) vem do vínculo da
// Convenia, porque cada um usa um modelo diferente.

// `estagioId` (opcional) prende o anexo a um PERÍODO de estágio (bporh_estagio.id):
// vai no GET (lista só o termo daquele período) e no FormData do POST (o
// backend recusa estágio sem ele). Sem a prop, admissão/rescisão/prorrogação
// seguem como sempre.
const ContratoAnexo = ({ tipo, clienteId, conveniaId, colaborador, vinculo, onMudou, aviso,
  onEstado, estagioId }) => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [anexos, setAnexos] = useState([]);
  // o PDF só é exigido em processos recentes (o backend define a janela);
  // admissão/rescisão antiga não trava o operador
  const [obrigatorio, setObrigatorio] = useState(false);
  // PJ não tem contrato de emprego a anexar aqui: o bloco vira só informativo
  const [exigeVinculo, setExigeVinculo] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  // id do anexo cuja assinatura está sendo confirmada/reconferida
  const [assinando, setAssinando] = useState(null);
  const inputRef = React.useRef(null);

  const carregar = useCallback(() => {
    if (!clienteId || !conveniaId) return;
    axios.get(`${API}/api/bporh/contrato/`,
      { params: { tipo, cliente_id: clienteId, convenia_id: conveniaId,
                  ...(estagioId ? { estagio_id: estagioId } : {}) }, headers: headers() })
      .then((res) => {
        setAnexos(res.data.anexos || []);
        setObrigatorio(Boolean(res.data.obrigatorio));
        setExigeVinculo(res.data.exige_vinculo !== false);
      })
      .catch(() => setAnexos([]));
  }, [tipo, clienteId, conveniaId, estagioId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  // avisa a tela: sem PDF anexado o botão "Concluído" da tarefa fica travado,
  // e destrava sozinho assim que o contrato entra
  useEffect(() => {
    if (onEstado) onEstado({ tem: anexos.length > 0, obrigatorio, exige: exigeVinculo });
  }, [anexos, obrigatorio, exigeVinculo]); // eslint-disable-line react-hooks/exhaustive-deps

  const enviar = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setErro('O contrato deve ser um PDF'); return;
    }
    const MAX_MB = 40;
    if (file.size > MAX_MB * 1024 * 1024) {
      setErro(`PDF de ${(file.size / 1024 / 1024).toFixed(1)}MB — o máximo é ${MAX_MB}MB. `
        + 'Comprima o arquivo (ex.: ilovepdf.com/compress_pdf) ou reduza a resolução do scan.');
      return;
    }
    setErro('');
    setEnviando(true);

    // registra os metadados depois que o PDF já está no bucket (ou envia o
    // arquivo junto, no caminho tradicional)
    const registrar = (caminho) => {
      const fd = new FormData();
      fd.append('tipo', tipo);
      fd.append('cliente_id', clienteId);
      fd.append('convenia_id', conveniaId);
      // só o POST lê o período: o upload-url ignora o campo
      if (estagioId) fd.append('estagio_id', estagioId);
      if (colaborador) fd.append('colaborador', colaborador);
      if (vinculo) fd.append('tipo_contrato', vinculo);
      if (caminho) { fd.append('caminho', caminho); fd.append('nome', file.name); }
      else fd.append('arquivo', file);
      return axios.post(`${API}/api/bporh/contrato/`, fd, { headers: headers() })
        .then((res) => {
          setAnexos(res.data.anexos || []);
          // avisa na hora quando o PDF anexado não parece assinado
          if ((res.data.assinatura?.problemas || []).length) {
            setErro(res.data.mensagem || 'O PDF anexado não passou na conferência');
          }
          if (onMudou) onMudou(res.data.anexos || []);
        });
    };

    // Caminho preferencial: PUT direto no bucket (não passa pelo backend, então
    // não esbarra no limite de requisição da plataforma). Se a assinatura não
    // estiver disponível no ambiente, envia pelo backend mesmo.
    axios.post(`${API}/api/bporh/contrato/upload-url/`,
      { tipo, cliente_id: clienteId, convenia_id: conveniaId }, { headers: headers() })
      .then((res) => axios.put(res.data.url, file, {
        headers: { 'Content-Type': 'application/pdf' },
      }).then(() => registrar(res.data.caminho)))
      .catch(() => registrar(null))
      .catch((error) => setErro(error.response?.data?.mensagem || 'Falha ao anexar'))
      .finally(() => setEnviando(false));
  };

  // A conferência é apoio, não juiz: quando ela não decide (PDF digitalizado,
  // plataforma desconhecida), o operador confirma — e fica registrado quem foi.
  const marcarAssinatura = (a, acao) => {
    setAssinando(a.id);
    setErro('');
    axios.post(`${API}/api/bporh/contrato/${a.id}/assinatura/`, { acao },
      { headers: headers() })
      .then(() => carregar())
      .then(() => { if (onMudou) onMudou(); })
      .catch((error) => setErro(error.response?.data?.mensagem
        || 'Não foi possível atualizar a assinatura'))
      .finally(() => setAssinando(null));
  };

  const baixar = (a) => {
    axios.get(`${API}/api/bporh/contrato/${a.id}/`, { headers: headers(), responseType: 'blob' })
      .then((res) => window.open(URL.createObjectURL(res.data), '_blank'))
      .catch(() => setErro('Não foi possível abrir o PDF'));
  };

  const remover = (a) => {
    if (!window.confirm(`Remover o contrato "${a.nome}"?`)) return;
    axios.delete(`${API}/api/bporh/contrato/${a.id}/`, { headers: headers() })
      .then(() => carregar())
      .then(() => { if (onMudou) onMudou(); })
      .catch(() => setErro('Não foi possível remover'));
  };

  return (
    <div className="mt-1">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <input type="file" accept="application/pdf,.pdf" ref={inputRef}
          onChange={enviar} style={{ display: 'none' }} />
        <button type="button" className="btn btn-sm btn-outline-primary"
          style={{ fontSize: '.76rem', padding: '2px 8px' }}
          disabled={enviando || !conveniaId}
          onClick={() => inputRef.current?.click()}
          title={vinculo
            ? `Anexar o contrato assinado (modelo ${vinculo}) em PDF`
            : 'Anexar o contrato assinado em PDF'}>
          {enviando
            ? <><span className="spinner-border spinner-border-sm me-1"></span>Enviando...</>
            : <><i className="fas fa-file-arrow-up me-1"></i>Anexar PDF</>}
        </button>
        {vinculo && (
          <span className="badge" style={{ background: '#eef2f7', color: '#00205b', border: '1px solid #d6dee8' }}
            title="Cada vínculo usa um modelo de contrato diferente — confira antes de anexar">
            modelo {vinculo}
          </span>
        )}
        {anexos.length === 0 && aviso !== false && (
          !exigeVinculo ? (
            <span className="small text-muted"
              title="O PJ presta serviço por contrato próprio, que não entra no checklist do DP">
              <i className="fas fa-circle-info me-1"></i>
              {vinculo ? `${vinculo} não exige contrato aqui` : 'vínculo não exige contrato'}
              {' '}— anexo opcional
            </span>
          ) : obrigatorio ? (
            <span className="small" style={{ color: '#8a6d00' }}>
              <i className="fas fa-triangle-exclamation me-1"></i>
              obrigatório para concluir
            </span>
          ) : (
            <span className="small text-muted"
              title="Sem o PDF esta tarefa não pode ser marcada como concluída — em processo antigo, use 'Não se aplica'">
              <i className="fas fa-circle-info me-1"></i>
              anexe para concluir (ou marque “Não se aplica”)
            </span>
          )
        )}
      </div>

      {anexos.map(a => (
        <div key={a.id} className="d-flex align-items-center gap-2 mt-1 small">
          <i className="fas fa-file-pdf" style={{ color: '#a11d2b' }}></i>
          <button type="button" className="btn btn-link btn-sm p-0"
            style={{ fontSize: '.78rem', textDecoration: 'none' }}
            onClick={() => baixar(a)} title="Abrir o PDF">
            {a.nome}
          </button>
          <span className="text-muted" style={{ fontSize: '.72rem' }}>
            {a.tipo_contrato ? `${a.tipo_contrato} · ` : ''}{a.enviado_em}
            {a.enviado_por ? ` · ${a.enviado_por}` : ''}
          </span>
          {/* veredito da conferência: documento certo, pessoa certa e assinado
              pelas duas partes. Um problema por badge, para o operador saber o
              que corrigir sem abrir o PDF. */}
          {(() => {
            const v = a.assinatura || {};
            const problemas = v.problemas || [];
            if (problemas.length) {
              return problemas.map((p, i) => (
                <span key={i} className="badge" title={v.motivo || p}
                  style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #f3b8bf' }}>
                  <i className="fas fa-circle-exclamation me-1"></i>{p}
                </span>
              ));
            }
            if (a.assinado === true) {
              // nomes quando a IA os leu; senão, os papéis do log da plataforma
              const quem = ((v.signatarios || []).length ? v.signatarios : (v.papeis_log || [])).join(', ');
              return (
                <span className="badge"
                  title={[v.motivo, quem && `Assinaram: ${quem}`,
                    a.assinatura_por && `confirmado por ${a.assinatura_por}`]
                    .filter(Boolean).join(' · ')}
                  style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #a8dcc3' }}>
                  <i className="fas fa-circle-check me-1"></i>
                  conferido{v.plataforma ? ` · ${v.plataforma}` : ''}
                  {v.ambas_partes ? ' · ambas as partes' : ''}
                </span>
              );
            }
            return (
              <span className="badge" title={v.motivo
                || 'A conferência automática não conseguiu decidir'}
                style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f0dfa8' }}>
                <i className="fas fa-question me-1"></i>não conferido
              </span>
            );
          })()}
          {a.assinado !== true && (
            <button type="button" className="btn btn-sm btn-light border p-1 px-2"
              style={{ fontSize: '.7rem' }}
              disabled={assinando === a.id}
              onClick={() => marcarAssinatura(a, 'confirmar')}
              title="Conferi o documento: está assinado">
              {assinando === a.id
                ? <span className="spinner-border spinner-border-sm"></span>
                : <>confirmar assinatura</>}
            </button>
          )}
          {a.assinado !== null && (
            <button type="button" className="btn btn-sm btn-light border p-1"
              disabled={assinando === a.id}
              onClick={() => marcarAssinatura(a, 'reconferir')}
              title="Conferir de novo pelo sistema">
              <i className="fas fa-rotate" style={{ fontSize: '.7rem' }}></i>
            </button>
          )}
          <button type="button" className="btn btn-sm btn-light p-1 border"
            onClick={() => remover(a)} title="Remover este contrato">
            <i className="fas fa-trash text-danger" style={{ fontSize: '.7rem' }}></i>
          </button>
        </div>
      ))}

      {erro && <div className="small text-danger mt-1">{erro}</div>}
    </div>
  );
};

export default ContratoAnexo;
